// Epic 8 admin QoL — bulk approve/reject on the creator-application queue.
//
// Creator approval/rejection are inherently PER-APPLICANT operations: each
// fires its own welcome/rejection email, and an approval additionally mints
// a welcome dossier + assigns a unique handle. There is therefore nothing
// to gain from a bespoke atomic server endpoint — a "bulk approve" is N
// independent transactions no matter where the loop lives. So this fans out
// over the SAME single-application endpoints the detail page uses
// (POST /admin/creator-applications/:id/{approve,reject}), each already
// tested. The loop is SEQUENTIAL, not a concurrent Promise.all: each
// decision is a heavy admin write (approval mints a welcome dossier + a
// handle + an email) and the server enforces an admin-write rate limit —
// a concurrent burst would trip it and spuriously fail rows. One-at-a-time
// stays under the limiter while still collecting per-id outcomes so the
// caller can show a partial-success summary; one failure never blocks the
// rest.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type BulkReviewOutcome = {
  succeeded: string[];
  failed: { applicationId: string; message: string }[];
};

export type BulkReviewVariables =
  | { decision: "approve"; ids: string[] }
  | { decision: "reject"; ids: string[]; reason: string };

async function postDecision(
  applicationId: string,
  decision: "approve" | "reject",
  reason: string | undefined,
): Promise<void> {
  const response = await fetch(
    `/api/v1/admin/creator-applications/${applicationId}/${decision}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        // Fresh key per applicant — matches the single-decision hooks.
        "X-Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify(decision === "reject" ? { reason } : {}),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON — keep the status-code fallback.
    }
    throw new Error(message);
  }
}

async function fanOut(vars: BulkReviewVariables): Promise<BulkReviewOutcome> {
  const reason = vars.decision === "reject" ? vars.reason : undefined;
  const outcome: BulkReviewOutcome = { succeeded: [], failed: [] };
  for (const id of vars.ids) {
    try {
      await postDecision(id, vars.decision, reason);
      outcome.succeeded.push(id);
    } catch (e) {
      outcome.failed.push({
        applicationId: id,
        message: e instanceof Error ? e.message : "Failed",
      });
    }
  }
  return outcome;
}

export function useBulkReviewCreatorApplications() {
  const queryClient = useQueryClient();
  return useMutation<BulkReviewOutcome, Error, BulkReviewVariables>({
    mutationFn: fanOut,
    // Refetch the queue once, after the whole batch settles, regardless of
    // partial failures — acted-on rows leave the "pending" filter.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-creator-applications"],
      });
    },
  });
}
